import * as React from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { useIntl, type IntlShape } from 'react-intl'
import moment from 'moment'

import BodyText from 'LuxKit/Typography/BodyText'
import NestableClickable from 'components/Common/NestableClickable'
import OptionsButton from 'tripPlanner/components/Documents/OptionsButton'
import { OptionsMenuActions, OptionsMenuButton } from 'tripPlanner/components/Documents/OptionsMenuActions'
import type { RecentChatItem } from 'tripPlanner/pages/chat/recentChatItems'
import RecentItemInlineTitleEdit from './RecentItemInlineTitleEdit'
import { intlDateFormatter } from 'lib/datetime/dateUtils'
import { INTL_DAY_MONTH_NAME_SHORT, INTL_DMY_CASUAL_SHORT_FORMAT } from 'constants/dateFormats'

const Container = styled(NestableClickable)`
  display: block;
  border-bottom: 1px solid ${props => props.theme.palette.neutral.default.six};
  padding: ${rem(12)} 0;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: ${props => props.theme.palette.neutral.default.seven};
  }
`

const Content = styled.div`
  display: flex;
  gap: ${rem(8)};
  align-items: flex-start;
  justify-content: space-between;
  min-width: 0;
`

const TextContent = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
  padding-inline-start: ${rem(20)};
`

const Description = styled(BodyText)`
  margin-top: ${rem(4)};
`

const LastActivity = styled.div<{ $withSeparator: boolean }>`
  display: grid;
  grid-template-columns: ${props => props.$withSeparator ? 'auto auto minmax(0, 1fr)' : 'minmax(0, 1fr)'};
  align-items: baseline;
  column-gap: ${rem(4)};
  width: 100%;
  min-width: 0;
  margin-top: ${rem(6)};
`

const LastMessage = styled(BodyText)`
  min-width: 0;
`

const Separator = styled.span`
  color: ${props => props.theme.palette.neutral.default.three};
  font-family: ${props => props.theme.font.primary.family};
  font-size: 12px;
  line-height: 16px;

  &::before {
    content: '·';
  }
`

interface Props {
  item: RecentChatItem
  subtitle?: string
  description?: string
  to?: string
  onClick?: React.MouseEventHandler
  onRename: (item: RecentChatItem, name: string) => Promise<void>
  onDeleteClick: (item: RecentChatItem, closeMenu: () => void) => (event: React.MouseEvent) => void
}

export function getCompactRecentChatTimestampLabel(updatedAt: string, intl: IntlShape, now = moment()): string {
  const updatedAtMoment = moment(updatedAt)
  if (!updatedAtMoment.isValid()) {
    return intlDateFormatter(updatedAt, INTL_DAY_MONTH_NAME_SHORT, intl.locale)
  }

  const dateFormat = updatedAtMoment.year() === now.year() ? INTL_DAY_MONTH_NAME_SHORT : INTL_DMY_CASUAL_SHORT_FORMAT
  if (updatedAtMoment.isAfter(now)) {
    return intlDateFormatter(updatedAt, dateFormat, intl.locale)
  }

  const elapsed = moment.duration(now.diff(updatedAtMoment))
  const elapsedMinutes = elapsed.asMinutes()
  const elapsedHours = elapsed.asHours()
  const daysAgo = now.diff(updatedAtMoment, 'days')

  if (elapsedMinutes < 60) {
    return intl.formatMessage(
      {
        id: 'ypnHv6MlPJK7huj2MeOn',
        defaultMessage: '{count}m',
        description: 'Compact minutes-since label in the Trip Planner chat drawer, such as 1m or 34m',
      },
      { count: Math.max(1, Math.floor(elapsedMinutes)) },
    )
  }

  if (elapsedHours < 24) {
    return intl.formatMessage(
      {
        id: 'r3w7lFVyL-rGfdUBXZyf',
        defaultMessage: '{count}h',
        description: 'Compact hours-since label in the Trip Planner chat drawer, such as 1h or 7h',
      },
      { count: Math.max(1, Math.min(23, Math.round(elapsedHours))) },
    )
  }

  if (daysAgo <= 7) {
    return intl.formatMessage(
      {
        id: 'UopsFCaBxwLeFeITP77a',
        defaultMessage: '{count}d',
        description: 'Compact days-since label in the Trip Planner chat drawer, such as 1d or 7d',
      },
      { count: daysAgo },
    )
  }

  return intlDateFormatter(updatedAt, dateFormat, intl.locale)
}

export default function DrawerRecentChatListItem({ item, subtitle, description, to, onClick, onRename, onDeleteClick }: Props) {
  const intl = useIntl()
  const [isRenaming, setIsRenaming] = React.useState(false)
  const formattedTimestamp = getCompactRecentChatTimestampLabel(item.updatedAt, intl)
  const fallbackTitle = intl.formatMessage({
    id: 'P7xyHLCCjSlOklqXsVYg',
    defaultMessage: 'Untitled',
    description: 'Default title for untitled trip in the trip planner feature',
  })
  const actionsTitle = item.type === 'classicTrip' ?
      intl.formatMessage({
        id: '3VJ6QlvfdQdt7iHnZ58y',
        defaultMessage: 'Trip actions',
        description: 'Accessible title for the trip options menu button in the drawer',
      }) :
      intl.formatMessage({
        id: '-bD6A4Qe-0a_1LR2lLWn',
        defaultMessage: 'Chat actions',
        description: 'Accessible title for the chat actions dropdown menu in the drawer list item',
      })
  const deleteLabel = item.type === 'classicTrip' ?
      intl.formatMessage({
        id: 'Jp_lIRegyPJ1-xyn0DHR',
        defaultMessage: 'Delete',
        description: 'Delete button label in the trip actions menu in the drawer',
      }) :
      intl.formatMessage({
        id: 'NYvcrggtkoIHuLPifYLV',
        defaultMessage: 'Delete',
        description: 'Button label to delete a chat conversation from the TripPlanner drawer list',
      })

  const renameLabel = intl.formatMessage({
    id: 'nxJehzKwdB_IfLuVEgrw',
    defaultMessage: 'Rename',
    description: 'Button label to rename a chat or trip from the TripPlanner drawer list',
  })

  const handleRenameClick = (closeMenu: () => void) => (event: React.MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    closeMenu()
    setIsRenaming(true)
  }

  const handleRename = React.useCallback((name: string) => {
    return onRename(item, name)
  }, [item, onRename])

  return (
    <Container to={to} onClick={onClick}>
      <Content>
        <TextContent>
          <RecentItemInlineTitleEdit
            title={item.title}
            fallbackTitle={fallbackTitle}
            isEditing={isRenaming}
            variant="medium"
            onEditingChange={setIsRenaming}
            onRename={handleRename}
          />
          {!!description && (
            <Description variant="small" colour="normal-contrast" lineClamp={2}>
              {description}
            </Description>
          )}
          {(formattedTimestamp || subtitle) && (
            <LastActivity $withSeparator={!!formattedTimestamp && !!subtitle}>
              {!!formattedTimestamp && (
                <BodyText variant="small" colour="light-foreground-alt" as="span">
                  {formattedTimestamp}
                </BodyText>
              )}
              {!!formattedTimestamp && !!subtitle && (
                <Separator aria-hidden />
              )}
              {!!subtitle && (
                <LastMessage variant="small" colour="light-foreground-alt" italics lineClamp={1}>
                  {subtitle}
                </LastMessage>
              )}
            </LastActivity>
          )}
        </TextContent>
        <OptionsButton
          title={actionsTitle}
          bodyContent={(closeMenu) => (
            <OptionsMenuActions>
              <OptionsMenuButton
                type="button"
                onClick={handleRenameClick(closeMenu)}
              >
                {renameLabel}
              </OptionsMenuButton>
              <OptionsMenuButton type="button" onClick={onDeleteClick(item, closeMenu)}>
                {deleteLabel}
              </OptionsMenuButton>
            </OptionsMenuActions>
          )}
        />
      </Content>
    </Container>
  )
}
